/**
 * The twelve slide templates are settled design (Figma file 4zetjKJwDVnCHlpSx78Ozz,
 * page "Slides"). Their ids are a wire contract - a saved slide stores one of
 * these strings - so all twelve are declared up front even though the registry
 * below is filled in one phase at a time. A slide pointing at an id that has no
 * descriptor yet is reported by validation instead of crashing the editor.
 */
export enum PowerSlideTemplateId {
  Basic = "basic",
  Title = "title",
  Section = "section",
  KeyPoints = "keyPoints",
  Numbered = "numbered",
  ByTheNumbers = "byTheNumbers",
  Image = "image",
  SideBySide = "sideBySide",
  ThreeVisuals = "threeVisuals",
  Gallery = "gallery",
  OverlayImage = "overlayImage",
  Video = "video",
}

/**
 * Every template carries a Title. Slides have no separate name: the rail label,
 * the sequence graph node and validation messages all label a slide by it.
 */
export const POWER_SLIDE_TITLE_FIELD = "title";

/**
 * The template a deck starts on, and the one Add slide inserts. Defined here
 * rather than in either caller so the interaction's first slide and every later
 * one cannot drift apart.
 */
export const DEFAULT_POWER_SLIDE_TEMPLATE_ID = PowerSlideTemplateId.Title;

/**
 * The three surfaces a theme defines. A template declares which role it uses
 * and the theme resolves it, so a Section slide is dark because it is a chapter
 * break rather than because someone coloured it.
 */
export type PowerSlideSurface = "content" | "title" | "section";

export type PowerSlideAlignment = "left" | "center";

/**
 * Key Points and Numbered are the same layout with a different marker. They
 * stay two template ids - two picker cards, two saved values - while sharing
 * one renderer. The marker belongs to the template, never to the slide.
 */
export type PowerSlideListMarker = "bullet" | "number";

/**
 * How the renderer arranges a template's fields. Several templates share a
 * layout and differ only by parameter, which is why twelve template ids need
 * far fewer components.
 */
export type PowerSlideBodyLayout =
  | "stack"
  | "list"
  | "stats"
  /** One asset filling the slide's middle, with an optional caption below. */
  | "media"
  /** A grid of asset slots, optionally captioned per slot. */
  | "grid"
  /** Full-bleed asset with the text over a scrim. */
  | "overlay";

export interface PowerSlideTextField {
  id: string;
  /** English label, used as the field placeholder and in validation messages. */
  label: string;
  required?: boolean;
  /** A repeating field holds an ordered list of keys rather than a single one. */
  repeating?: boolean;
  /** Repeating fields only. minItems === maxItems means a fixed-length list. */
  minItems?: number;
  maxItems?: number;
  /** How many items a freshly created slide starts with. */
  defaultItems?: number;
  /**
   * Another repeating field whose item at the same index belongs with this
   * one. By the Numbers pairs `values` with `labels`, so adding a column has
   * to extend both lists: extend one alone and `values[2]` starts describing
   * `labels[3]`, silently, on every slide after the edit.
   *
   * The two lists stay parallel rather than becoming a list of pairs because
   * the wire format is already published and the runtime is being built
   * against it. Pairing is enforced here instead, in the one place that
   * mutates them.
   */
  pairedWith?: string;
}

export interface PowerSlideAssetSlot {
  id: string;
  label: string;
  /** Decides which asset type the file picker offers and how the slot reads. */
  kind: "image" | "video";
}

/**
 * A template describes what a slide can hold and how that content is presented,
 * never a concrete colour. Appearance belongs to the template and the deck
 * theme, which is why a slide carries no styling and needs no conversion when
 * pasted into a differently themed deck.
 */
export interface PowerSlideTemplate {
  id: PowerSlideTemplateId;
  /** English name; also the rail placeholder for a slide with no title yet. */
  name: string;
  /** i18n key for the picker card and rail chip, under powerslide.templates. */
  locId: string;
  /**
   * Filename under public/powerslide_previews/, exported from the template's
   * Runtime variant in Figma. Re-export when a template's design changes -
   * the recipe is in docs/PowerSlideImplementationPlan.md.
   */
  previewImage: string;
  surface: PowerSlideSurface;
  align: PowerSlideAlignment;
  bodyLayout: PowerSlideBodyLayout;
  listMarker?: PowerSlideListMarker;
  /** Grid layouts only: how many slots sit across a row. */
  gridColumns?: number;
  textFields: PowerSlideTextField[];
  assetSlots: PowerSlideAssetSlot[];
}

const imageSlot = (index: number): PowerSlideAssetSlot => ({
  id: `image${index}`,
  label: `Image ${index}`,
  kind: "image",
});

/** Per-slot captions, one per asset slot and fixed to that count. */
const captionsField = (count: number): PowerSlideTextField => ({
  id: "captions",
  label: "Caption",
  repeating: true,
  minItems: count,
  maxItems: count,
  defaultItems: count,
});

const titleField: PowerSlideTextField = {
  id: POWER_SLIDE_TITLE_FIELD,
  label: "Title",
  required: true,
};

/**
 * Secondary regions are optional and hidden when empty. There is no separate
 * "Show subtitle" flag in the data - clearing the text is what hides it - so a
 * slide stays pure content with nothing to keep in sync.
 */
const basicSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.Basic,
  name: "Basic slide",
  locId: "basic",
  previewImage: "basic.webp",
  surface: "content",
  align: "left",
  bodyLayout: "stack",
  textFields: [titleField, { id: "body", label: "Body" }],
  assetSlots: [],
};

const titleSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.Title,
  name: "Title slide",
  locId: "title",
  previewImage: "title.webp",
  surface: "title",
  align: "center",
  bodyLayout: "stack",
  textFields: [titleField, { id: "subtitle", label: "Subtitle" }],
  assetSlots: [],
};

const sectionSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.Section,
  name: "Section slide",
  locId: "section",
  previewImage: "section.webp",
  surface: "section",
  align: "center",
  bodyLayout: "stack",
  textFields: [
    { id: "eyebrow", label: "Eyebrow" },
    titleField,
    { id: "subtitle", label: "Subtitle" },
  ],
  assetSlots: [],
};

const keyPointsSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.KeyPoints,
  name: "Key points slide",
  locId: "keyPoints",
  previewImage: "keyPoints.webp",
  surface: "content",
  align: "left",
  bodyLayout: "list",
  listMarker: "bullet",
  textFields: [
    titleField,
    {
      id: "bullets",
      label: "Bullet",
      repeating: true,
      minItems: 1,
      maxItems: 6,
      defaultItems: 4,
    },
  ],
  assetSlots: [],
};

const numberedSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.Numbered,
  name: "Numbered slide",
  locId: "numbered",
  previewImage: "numbered.webp",
  surface: "content",
  align: "left",
  bodyLayout: "list",
  listMarker: "number",
  textFields: [
    titleField,
    {
      id: "steps",
      label: "Step",
      repeating: true,
      minItems: 1,
      maxItems: 6,
      defaultItems: 4,
    },
  ],
  assetSlots: [],
};

/**
 * Values and labels are two parallel fixed-length lists rather than a list of
 * pairs, which keeps every field a flat key list. They cannot drift because the
 * column count is fixed at three - there is no add or remove control.
 */
const byTheNumbersSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.ByTheNumbers,
  name: "By the numbers slide",
  locId: "byTheNumbers",
  previewImage: "byTheNumbers.webp",
  surface: "content",
  align: "left",
  bodyLayout: "stats",
  textFields: [
    titleField,
    // Two to four columns. Four is the ceiling because the value keeps its
    // 80px size at every count: a fifth column narrows the track to 195px,
    // where "1,250" wraps mid-figure. Stepping the type down to fit five was
    // tried and rejected - the figures stop carrying the slide, which is the
    // only thing this template is for.
    {
      id: "values",
      label: "Value",
      repeating: true,
      minItems: 2,
      maxItems: 4,
      defaultItems: 3,
      pairedWith: "labels",
    },
    {
      id: "labels",
      label: "Label",
      repeating: true,
      minItems: 2,
      maxItems: 4,
      defaultItems: 3,
      pairedWith: "values",
    },
  ],
  assetSlots: [],
};

const imageSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.Image,
  name: "Image slide",
  locId: "image",
  previewImage: "image.webp",
  surface: "content",
  align: "left",
  bodyLayout: "media",
  textFields: [titleField, { id: "caption", label: "Caption" }],
  assetSlots: [{ id: "image", label: "Image", kind: "image" }],
};

/**
 * No caption, deliberately. Dropping it grew the media region from 2.71:1 to
 * 2.22:1, and a procedural clip is worth more height than a line of prose.
 */
const videoSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.Video,
  name: "Video slide",
  locId: "video",
  previewImage: "video.webp",
  surface: "content",
  align: "left",
  bodyLayout: "media",
  textFields: [titleField],
  assetSlots: [{ id: "video", label: "Video", kind: "video" }],
};

const sideBySideSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.SideBySide,
  name: "Side by side slide",
  locId: "sideBySide",
  previewImage: "sideBySide.webp",
  surface: "content",
  align: "left",
  bodyLayout: "grid",
  gridColumns: 2,
  textFields: [titleField, captionsField(2)],
  assetSlots: [imageSlot(1), imageSlot(2)],
};

const threeVisualsSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.ThreeVisuals,
  name: "Three visuals slide",
  locId: "threeVisuals",
  previewImage: "threeVisuals.webp",
  surface: "content",
  align: "left",
  bodyLayout: "grid",
  gridColumns: 3,
  textFields: [titleField, captionsField(3)],
  assetSlots: [imageSlot(1), imageSlot(2), imageSlot(3)],
};

/**
 * No per-slot captions: with four tiles they crowd the grid, and a gallery is a
 * set of images rather than an annotated comparison.
 */
const gallerySlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.Gallery,
  name: "Gallery slide",
  locId: "gallery",
  previewImage: "gallery.webp",
  surface: "content",
  align: "left",
  bodyLayout: "grid",
  gridColumns: 2,
  textFields: [titleField],
  assetSlots: [imageSlot(1), imageSlot(2), imageSlot(3), imageSlot(4)],
};

/**
 * The one template that ignores slide padding: the artwork is full bleed, so
 * the text sits over a scrim at a tighter 52px inset instead.
 */
const overlayImageSlideTemplate: PowerSlideTemplate = {
  id: PowerSlideTemplateId.OverlayImage,
  name: "Overlay image slide",
  locId: "overlayImage",
  previewImage: "overlayImage.webp",
  surface: "section",
  align: "left",
  bodyLayout: "overlay",
  textFields: [titleField, { id: "caption", label: "Caption" }],
  assetSlots: [{ id: "image", label: "Image", kind: "image" }],
};

/**
 * Picker order: reading order beats categorisation in a grid, so related
 * templates cluster without being labelled. Text templates first, then media.
 */
const powerSlideTemplates: Partial<
  Record<PowerSlideTemplateId, PowerSlideTemplate>
> = {
  [PowerSlideTemplateId.Title]: titleSlideTemplate,
  [PowerSlideTemplateId.Section]: sectionSlideTemplate,
  [PowerSlideTemplateId.Basic]: basicSlideTemplate,
  [PowerSlideTemplateId.KeyPoints]: keyPointsSlideTemplate,
  [PowerSlideTemplateId.Numbered]: numberedSlideTemplate,
  [PowerSlideTemplateId.ByTheNumbers]: byTheNumbersSlideTemplate,
  [PowerSlideTemplateId.Image]: imageSlideTemplate,
  [PowerSlideTemplateId.SideBySide]: sideBySideSlideTemplate,
  [PowerSlideTemplateId.ThreeVisuals]: threeVisualsSlideTemplate,
  [PowerSlideTemplateId.Gallery]: gallerySlideTemplate,
  [PowerSlideTemplateId.OverlayImage]: overlayImageSlideTemplate,
  [PowerSlideTemplateId.Video]: videoSlideTemplate,
};

export const DEFAULT_POWER_SLIDE_TEMPLATE = PowerSlideTemplateId.Basic;

export function getPowerSlideTemplate(
  templateId: PowerSlideTemplateId | string
): PowerSlideTemplate | undefined {
  return powerSlideTemplates[templateId as PowerSlideTemplateId];
}

/** The templates the editor can currently offer, in picker order. */
export function getRegisteredPowerSlideTemplates(): PowerSlideTemplate[] {
  return Object.values(powerSlideTemplates) as PowerSlideTemplate[];
}
